import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';

import { StateFilterChain } from './state-filter-chain';
import {
    AnonymousFilter,
    AuthenticatedFilter,
    LoginFilter,
    RoleBasedFilter,
    RejectFilter,
    ZoneBasedFilter,
} from './state-filter';
import { LocalStorageService } from '@services/local-storage.service';
import { NotificationService } from '@notifications';
import { AuthenticationService } from '@auth/authentication.service';
import { ClientManagementService } from '@services/client-management.service';
import { SidebarService } from '@services/sidebar.service';

/**
 * implementation of CanActivate and CanActivateChild guard which makes decisions about
 * state activation based on security polices defined in state metadata
 */
@Injectable({
    providedIn: 'root'
})
export class AuthGuard  {
    private filterChain: StateFilterChain;

    constructor(
        private localStorageService: LocalStorageService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private notificationService: NotificationService,
        private clientManagementService: ClientManagementService,
        private sidebarService: SidebarService,
    ) {
        this.filterChain = new StateFilterChain()
            .registerFilter(new LoginFilter(this.localStorageService, this.router))
            .registerFilter(new AnonymousFilter(this.localStorageService))
            .registerFilter(new AuthenticatedFilter(
                this.authenticationService, this.localStorageService, this.notificationService, this.router,
                this.clientManagementService, this.sidebarService
            ))
            .registerFilter(new RoleBasedFilter(
                this.authenticationService, this.localStorageService, this.notificationService, this.router
            ))
            .registerFilter(new ZoneBasedFilter(this.localStorageService))
            .registerFilter(new RejectFilter());
    }

    /**
     * authentication guard for routes
     * @param route
     * @param state
     */
    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ) {
        if (!this.filterChain.executeChain(route, state)) {
            return this.router.parseUrl('/');
        }
        return true;
    }

    /**
     * authentication guard for child routes
     * @param childRoute
     * @param state
     */
    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ) {
        if (!this.filterChain.executeChain(childRoute, state)) {
            return this.router.parseUrl('/');
        }
        return true;
    }
}
